import {FormArray, FormControl, FormGroup} from '@angular/forms';

import {map} from 'rxjs/operators';
import {forkJoin, Observable} from 'rxjs';

import * as moment from 'moment';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    TenantsEnum,
    IndividualSourceOfFundsService,
    IndividualSourceOfFundInterface,
    SourceOfFundModel,
    IndividualExtendedModel,
    IndividualSourceOfFundModel,
    IndividualSourceOfFundInputModel,
} from '@spa/api';

import {DuplicatedValueValidatorKey, InArrayValidator, MaxLengthValidatorKey} from '@spa/validators';

import {BaseControl, Helper, SelectControl} from 'appShared/classes';
 

import {
    SourceOfFundControls,
    SourceOfFundSelector,
    SourceOfFundIdSelector,
    SourceOfFundFromSelector,
    SourceOfFundToSelector,
    SourceOfFundTypeSelector,
    SourceOfFundsStringMaxLength,
    SourceOfFundTypeSelectSelector,
    SourceOfFundsFormArraySelector,
    SourceOfFundIdentityNameSelector,
} from 'appAssets/descriptors';

import {environment} from 'appEnv/environment';
import { SourceOfFundsTrait } from 'appEntries/panel/traits';

export class SourceOfFundsForm {
    private readonly individual: IndividualExtendedModel;
    private readonly individualSourceOfFundsService: IndividualSourceOfFundsService;
    private readonly sourceOfFundsList: SourceOfFundModel[];

    public readonly isAlaricTenant: boolean;
    public readonly sourceOfFundsFormArraySelector: string;

    public form: FormGroup;
    public formsControlGroups: Array<BaseControl[]>;
    public visibleIdentityName: boolean;
    public visibleFundType: boolean;
    public visibleFundTypeSelect: boolean;

    constructor(
        individual: IndividualExtendedModel,
        sourceOfFundsList: SourceOfFundModel[],
        individualSourceOfFundsService: IndividualSourceOfFundsService
    ) {
        this.individual = individual;
        this.sourceOfFundsList = sourceOfFundsList;
        this.individualSourceOfFundsService = individualSourceOfFundsService;

        this.visibleFundType = true;
        this.visibleIdentityName = true;
        this.visibleFundTypeSelect = true;
        this.sourceOfFundsFormArraySelector = SourceOfFundsFormArraySelector;
        this.isAlaricTenant = Helper.enumsHelper().getDefaultTenantId() === TenantsEnum.Alaric;
    }

    private createFormGroup(model: IndividualSourceOfFundModel = null): FormGroup {
        const formGroup = new FormGroup({});

        const formsControlGroup = SourceOfFundControls(this.isAlaricTenant).map((control: BaseControl) => {
            if (model) {
                control.value = this.getSourceOfFundProperty(control.name, model);
            }

            if (control.name === SourceOfFundSelector) {
                const sourceOfFundIds = this.sourceOfFundsList.map((listItem: SourceOfFundModel) => listItem.id);

                control.extendValidators(InArrayValidator(sourceOfFundIds));

                (control as SelectControl).options = this.sourceOfFundsList.map((listItem: SourceOfFundModel) => {
                    return {
                        label: listItem.name,
                        value: listItem.id
                    };
                });
            }

            formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });

        this.formsControlGroups.push(formsControlGroup);

        return formGroup;
    }

    private getSourceOfFundProperty(selector: string, model: IndividualSourceOfFundModel): string | number {
        switch (selector) {
            case SourceOfFundIdSelector:
                return model.id;
            case SourceOfFundSelector:
                return model.sourceOfFundId;
            case SourceOfFundFromSelector:
                return model.getFrom || null;
            case SourceOfFundToSelector:
                return model.getTo || null;
            case SourceOfFundIdentityNameSelector:
                return model.identityName || null;
            case SourceOfFundTypeSelector:
            case SourceOfFundTypeSelectSelector:
                return model.fundType || null;
            default:
                return null;
        }
    }

    private initControlsPlaceHoldersAndVisibility(value, controlsGroup: BaseControl[]): void {
        const fromDateControl = controlsGroup[2];
        const toDateControl = controlsGroup[3];
        const identityNameControl = controlsGroup[4];
        const fundTypeControl = controlsGroup[5];
        const fundTypeSelectControl = controlsGroup[6] as SelectControl;

        identityNameControl.setHidden(true);
        fundTypeControl.setHidden(true);
        fundTypeSelectControl.setHidden(true);

        fromDateControl.placeholder = (!SourceOfFundsTrait.getInheritanceIds().includes(value)) ? 'From' : 'Year of birth of the deceased person ';
        toDateControl.placeholder = (!SourceOfFundsTrait.getInheritanceIds().includes(value)) ? 'To' : 'Year of Inheritance';
        fundTypeSelectControl.placeholder = (!SourceOfFundsTrait.getLoanIds().includes(value))? 'Type' : 'Type of the lender';


        if (SourceOfFundsTrait.getAtHomeTradingIds().includes(value)) {
            identityNameControl.placeholder = "Broker’s Name";
            identityNameControl.setHidden(false);
        } else if (SourceOfFundsTrait.getSalaryIds().includes(value)) {
            identityNameControl.placeholder = "Name of Employer";
            fundTypeControl.placeholder = "Address of Employer";
            identityNameControl.setHidden(false);
            fundTypeControl.setHidden(false);
        } else if (SourceOfFundsTrait.getInheritanceIds().includes(value)) {
            identityNameControl.placeholder =
                "Full name of the deceased person";
            identityNameControl.setHidden(false);
        } else if (SourceOfFundsTrait.getFamilyMemberIds().includes(value)) {
            identityNameControl.placeholder = "Full Name of Family Member";
            identityNameControl.setHidden(false);
        } else if (
            SourceOfFundsTrait.getPensionBenefitProgramIds().includes(value)
        ) {
            fundTypeSelectControl.options = [
                {
                    value: "Pension",
                    label: "Pension",
                },
                {
                    value: "Social benefits",
                    label: "Social benefits",
                },
            ];
            fundTypeSelectControl.setHidden(false);
        } else if (
            SourceOfFundsTrait.getInvestmentsAndSavingsIds().includes(value)
        ) {
            fundTypeSelectControl.options = [
                {
                    value: "Real estate",
                    label: "Real estate",
                },
                {
                    value: "Financial instruments (stocks, bonds, etc.)",
                    label: "Financial instruments (stocks, bonds, etc.)",
                },
                {
                    value: "Dividends",
                    label: "Dividends",
                },
                {
                    value: "Other",
                    label: "Other",
                },
            ];
            fundTypeSelectControl.setHidden(false);
        } else if (SourceOfFundsTrait.getSelfEmploymentIds().includes(value)) {
            identityNameControl.placeholder = "Name of Customer";
            fundTypeControl.placeholder = "Address of Customer";
            identityNameControl.setHidden(false);
            fundTypeControl.setHidden(false);
        } else if (SourceOfFundsTrait.getLoanIds().includes(value)) {
            identityNameControl.placeholder = "Name of the lender";

            fundTypeSelectControl.options = [
                {
                    value: "Bank / other institution",
                    label: "Bank / other institution",
                },
                {
                    value: "Another person / entity",
                    label: "Another person / entity",
                },
            ];

            identityNameControl.setHidden(false);
            fundTypeSelectControl.setHidden(false);
        } 
            
    }

    private refreshInfoData(arrayIndex: number): void {
        const formArray = this.form.controls[this.sourceOfFundsFormArraySelector] as FormArray;
        const filteredArrayControls = formArray.controls[arrayIndex];

        if (filteredArrayControls) {
            const identityNameControl = filteredArrayControls.get(SourceOfFundIdentityNameSelector);
            const fundTypeControl = filteredArrayControls.get(SourceOfFundTypeSelector);
            const fundTypeSelectControl = filteredArrayControls.get(SourceOfFundTypeSelectSelector);

            if (identityNameControl) {
                identityNameControl.reset();
                identityNameControl.updateValueAndValidity();
            }

            if (fundTypeControl) {
                fundTypeControl.reset();
                fundTypeControl.updateValueAndValidity();
            }

            if (fundTypeSelectControl) {
                fundTypeSelectControl.reset();
                fundTypeSelectControl.updateValueAndValidity();
            }
        }
    }

    private getFormControlStatuses(
        formGroup: FormGroup,
        formGroupIndex: number,
        formGroupSourceOfFundIds: number[],
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }
    ): boolean[] {
        return Object.keys(formGroup.controls).map((controlKey: string, controlIndex: number) => {
            const formControl = formGroup.controls[controlKey];
            const control = this.formsControlGroups[formGroupIndex][controlIndex];

            if (formControl.invalid) {
                const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                    if (errorKey === MaxLengthValidatorKey) {
                        return messageFunction(control.placeholder, SourceOfFundsStringMaxLength)[errorKey];
                    }

                    return messageFunction(control.placeholder)[errorKey];
                });

                control.setErrors(errors);

                return false;
            }

            if (control.name === SourceOfFundSelector) {
                const matchedIndex = formGroupSourceOfFundIds.indexOf(control.value as number);

                if ((matchedIndex !== formGroupIndex) && control.value) {
                    control.setError(messageFunction(control.placeholder)[DuplicatedValueValidatorKey]);

                    return false;
                }
            }

            if (control.name === SourceOfFundToSelector) {
                const dateFromControl = this.formsControlGroups[formGroupIndex][2];
                const dateFromStamp = moment(dateFromControl.value).unix();
                const dateToStamp = moment(formControl.value).unix();

                if (dateToStamp < dateFromStamp) {
                    control.setError(`This field should bе bigger than ${dateFromControl.placeholder}`);

                    return false;
                }
            }

            return true;
        });
    }

    private removeEmptyGroups(emptyGroupIndexes: number[], formArray: FormArray): void {
        emptyGroupIndexes.sort((previousIndex: number, nextIndex: number) => {
            return nextIndex - previousIndex;
        }).map((index: number) => {
            formArray.removeAt(index);

            this.formsControlGroups.splice(index, 1);
        });
    }

    private createUpdateRequests(formArray: FormArray): Observable<ApiResponse<ApiError[] | boolean>>[] {
        return formArray.controls
            .filter((formGroup: FormGroup) => {
                return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                    return (formControlKey === SourceOfFundIdSelector) &&
                        formGroup.controls[formControlKey].value;
                }).length !== 0;
            })
            .filter(this.removeUnchangedSourceOfFunds.bind(this))
            .map((formGroup: FormGroup) => {
                const filteredSourceOfFunds = this.individual.sourceOfFunds.find((sourceOfFund: IndividualSourceOfFundModel) => {
                    return sourceOfFund.id === formGroup.controls[SourceOfFundIdSelector].value;
                });

                const clonedModel = filteredSourceOfFunds.serialize();

                const sourceOfFundId = formGroup.controls[SourceOfFundSelector].value;

                filteredSourceOfFunds.sourceOfFundId = sourceOfFundId;

                filteredSourceOfFunds.setFrom =
                    moment(formGroup.controls[SourceOfFundFromSelector].value).format(environment.dateFormatLocalDate);

                filteredSourceOfFunds.setTo =
                    moment(formGroup.controls[SourceOfFundToSelector].value).format(environment.dateFormatLocalDate);

                const {identityName, fundType} = this.getIdentityNameAndTypeValue(sourceOfFundId, formGroup);

                filteredSourceOfFunds.identityName = identityName;
                filteredSourceOfFunds.fundType = fundType;

                return this.individualSourceOfFundsService.update(filteredSourceOfFunds)
                    .pipe(
                        map(this.updateRequestMap.bind(this, clonedModel))
                    );
            });
    }

    private getIdentityNameAndTypeValue(sourceId: number, formGroup: FormGroup): { identityName: string, fundType: string } {
        let identityName: string = null;
        let fundType: string = null;
        if (
            SourceOfFundsTrait.getAtHomeTradingIds().includes(sourceId) ||
            SourceOfFundsTrait.getInheritanceIds().includes(sourceId) ||
            SourceOfFundsTrait.getFamilyMemberIds().includes(sourceId)
        ) {
            identityName =
                formGroup.controls[SourceOfFundIdentityNameSelector].value;
        } else if (
            SourceOfFundsTrait.getSalaryIds().includes(sourceId) ||
            SourceOfFundsTrait.getSelfEmploymentIds().includes(sourceId)
        ) {
            identityName =
                formGroup.controls[SourceOfFundIdentityNameSelector].value;
            fundType = formGroup.controls[SourceOfFundTypeSelector].value;
        } else if (
            SourceOfFundsTrait.getPensionBenefitProgramIds().includes(sourceId)
        ) {
            fundType = formGroup.controls[SourceOfFundTypeSelectSelector].value;
        } else if (
            SourceOfFundsTrait.getInvestmentsAndSavingsIds().includes(sourceId)
        ) {
            identityName = formGroup.controls[SourceOfFundTypeSelector].value;
            fundType = formGroup.controls[SourceOfFundTypeSelectSelector].value;
        } else if (SourceOfFundsTrait.getLoanIds().includes(sourceId)) {
            identityName =
                formGroup.controls[SourceOfFundIdentityNameSelector].value;
            fundType = formGroup.controls[SourceOfFundTypeSelectSelector].value;
        } else {
            identityName = null;
            fundType = null;
        }

        return {identityName, fundType};
    }

    private removeUnchangedSourceOfFunds(formGroup: FormGroup): boolean {
        const filteredSourceOfFunds =
            this.individual.sourceOfFunds.filter((sourceOfFund: IndividualSourceOfFundModel) => {
                return sourceOfFund.id === formGroup.controls[SourceOfFundIdSelector].value;
            });

        if (filteredSourceOfFunds.length === 0) {
            return false;
        }

        const valuesCheck = Object.keys(formGroup.controls).map((formControlKey: string) => {
            const formControlValue = formGroup.controls[formControlKey].value;

            return this.checkSourceOfFundProperty(
                formControlKey,
                formControlValue,
                filteredSourceOfFunds[0]
            );
        });

        return valuesCheck.indexOf(false) >= 0;
    }

    private checkSourceOfFundProperty(
        selector: string,
        formPropertyValue: number | string,
        model: IndividualSourceOfFundModel
    ): boolean {
        switch (selector) {
            case SourceOfFundIdSelector:
                return model.id === formPropertyValue;
            case SourceOfFundSelector:
                return model.sourceOfFundId === formPropertyValue;
            case SourceOfFundFromSelector:
                return model.getFrom === formPropertyValue;
            case SourceOfFundToSelector:
                return model.getTo === formPropertyValue;
            case SourceOfFundIdentityNameSelector:
                return model.identityName === formPropertyValue;
            case SourceOfFundTypeSelector:
                return model.fundType === formPropertyValue;
            default:
                return false;
        }
    }

    private updateRequestMap(
        clonedModel: IndividualSourceOfFundInterface,
        response: ApiResponse<ApiError[] | null>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            const model = new IndividualSourceOfFundModel(clonedModel);

            this.individual.sourceOfFunds = this.individual.sourceOfFunds
                .map((sourceOfFund: IndividualSourceOfFundModel) => {
                    if (sourceOfFund.id === model.id) {
                        sourceOfFund = model;
                    }

                    return sourceOfFund;
                });

            return Helper.errorsHelper().createErrorResponse<null>(response);
        }

        return Helper.createSuccessResponse<boolean>(true);
    }

    private createRegistrationRequests(formArray: FormArray): Observable<ApiResponse<ApiError[] | boolean>>[] {
        return formArray.controls
            .filter((formGroup: FormGroup) => {
                return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                    return (formControlKey === SourceOfFundIdSelector) &&
                        !formGroup.controls[formControlKey].value;
                }).length !== 0;
            })
            .map(this.createRegistrationRequest.bind(this));
    }

    private createRegistrationRequest(formGroup: FormGroup): Observable<ApiResponse<ApiError[] | boolean>> {
        const {
            identityName,
            fundType
        } = this.getIdentityNameAndTypeValue(formGroup.value[SourceOfFundSelector], formGroup);

        const fromDate = moment(formGroup.value[SourceOfFundFromSelector]).format(environment.dateFormatLocalDate);
        const toDate = moment(formGroup.value[SourceOfFundToSelector]).format(environment.dateFormatLocalDate);

        const inputModel = new IndividualSourceOfFundInputModel(
            this.individual.id,
            formGroup.value[SourceOfFundSelector],
            fromDate,
            toDate,
            identityName,
            fundType
        );

        return this.individualSourceOfFundsService.register(inputModel)
            .pipe(
                map(this.registrationRequestMap.bind(this))
            );
    }

    private registrationRequestMap(
        response: ApiResponse<ApiError[] | IndividualSourceOfFundInterface>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse<IndividualSourceOfFundInterface>(response);
        }

        const data = response.data as IndividualSourceOfFundInterface;
        const model = new IndividualSourceOfFundModel(data);

        this.individual.sourceOfFunds.push(model);

        return Helper.createSuccessResponse<boolean>(true);
    }

    initForm(): void {
        this.form = new FormGroup({});
        this.formsControlGroups = [];

        const formArray = new FormArray([]);

        if (this.individual && (this.individual.sourceOfFunds.length !== 0)) {
            this.individual.sourceOfFunds.forEach((sourceOfFund: IndividualSourceOfFundModel) => {
                const formGroup = this.createFormGroup(sourceOfFund);

                formArray.push(formGroup);
            });
        } else {
            const formGroup = this.createFormGroup();

            formArray.push(formGroup);
        }

        this.form.addControl(this.sourceOfFundsFormArraySelector, formArray);
    }

    prepareDynamicForm(value: number, arrayIndex: number): void {
        if (!value) {
            return;
        }

        this.initControlsPlaceHoldersAndVisibility(value, this.formsControlGroups[arrayIndex]);
    }

    onTypeChange(sourceId: number, arrayIndex: number): void {
        this.prepareDynamicForm(sourceId, arrayIndex);
        this.refreshInfoData(arrayIndex);
    }

    onSelectSubType(type: string, arrayIndex: number): void {
        const isOtherSelect = type === 'Other';

        if (isOtherSelect) {
            const typeControl = this.formsControlGroups[arrayIndex][5];

            if (typeControl) {
                typeControl.placeholder = 'Please fill in';
                typeControl.setHidden(false);
            }
        }
    }

    validateForm(messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }): boolean {
        const emptyFormGroupIndexes = [];
        const formArray = (this.form.controls[this.sourceOfFundsFormArraySelector] as FormArray);

        const formGroupSourceOfFundIds = formArray.controls.map((formGroup: FormGroup) => {
            return Object.keys(formGroup.controls)
                .filter((controlKey: string) => controlKey === SourceOfFundSelector)
                .map((controlKey: string) => formGroup.controls[controlKey].value);
        });

        const statuses = formArray.controls.map((formGroup: FormGroup, formGroupIndex: number) => {
            const emptyGroupCheck = Object.keys(formGroup.controls)
                .filter((controlKey: string) => controlKey !== SourceOfFundIdSelector)
                .map((controlKey: string) => formGroup.controls[controlKey].value === '');

            if ((emptyGroupCheck.indexOf(false) < 0) && (formGroupIndex > 0)) {
                emptyFormGroupIndexes.push(formGroupIndex);

                return true;
            }

            const formControlStatuses = this.getFormControlStatuses(
                formGroup,
                formGroupIndex,
                [].concat(...formGroupSourceOfFundIds),
                messageFunction
            );

            return formControlStatuses.indexOf(false) < 0;
        });

        if (emptyFormGroupIndexes.length !== 0) {
            this.removeEmptyGroups(emptyFormGroupIndexes, formArray);
        }

        return statuses.indexOf(false) < 0;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> | null {
        if (this.form.dirty) {
            const requests = [];

            const formArray = this.form.controls[this.sourceOfFundsFormArraySelector] as FormArray;

            if (this.individual && this.individual.sourceOfFunds.length !== 0) {
                const updateRequests = this.createUpdateRequests(formArray);

                if (updateRequests.length !== 0) {
                    requests.push(...updateRequests);
                }
            }

            const registrationRequests = this.createRegistrationRequests(formArray);

            if (registrationRequests.length !== 0) {
                requests.push(...registrationRequests);
            }

            if (requests.length !== 0) {
                return forkJoin(requests)
                    .pipe(
                        map((responses: ApiResponse<ApiError[] | boolean>[]) => {
                            if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
                                return Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
                            }

                            return Helper.createSuccessResponse<boolean>(true);
                        })
                    );
            }
        }

        return null;
    }
}

